import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import './Notifications.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const socket = useSocket();
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!socket) return;
    const handleRefresh = () => fetchNotifications();
    socket.on('new_bid', handleRefresh);
    socket.on('payment_uploaded', handleRefresh);
    socket.on('bid_accepted', handleRefresh);
    socket.on('contract_status_change', handleRefresh);
    socket.on('work_completed', handleRefresh);
    socket.on('payment_released', handleRefresh);
    return () => {
      socket.off('new_bid', handleRefresh);
      socket.off('payment_uploaded', handleRefresh);
      socket.off('bid_accepted', handleRefresh);
      socket.off('contract_status_change', handleRefresh);
      socket.off('work_completed', handleRefresh);
      socket.off('payment_released', handleRefresh);
    };
  }, [socket, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev =>
        prev.map(n => (n._id === notificationId ? { ...n, isRead: true } : n))
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(`${API_BASE}/notifications/read-all`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification._id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      bid: 'fa-gavel',
      payment: 'fa-wallet',
      contract: 'fa-file-contract',
      report: 'fa-file-alt',
      dispute: 'fa-scale-balanced',
      job: 'fa-briefcase',
      message: 'fa-envelope',
      system: 'fa-bell'
    };
    return icons[type] || 'fa-bell';
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading) return <div className="loader-container"><i className="fa-solid fa-spinner fa-spin"></i></div>;

  return (
    <div className="notif-page-wrapper">
      <div className="notif-header">
        <div className="notif-header-text">
          <h1><i className="fa-regular fa-bell"></i> Notifications</h1>
          <p>Stay updated on actions and system logs</p>
        </div>
        {unreadCount > 0 && (
          <button className="notif-btn-secondary" onClick={handleMarkAllAsRead}>
            <i className="fa-solid fa-check-double"></i> Mark all as read
          </button>
        )}
      </div>

      <div className="notif-list-container">
        {notifications.length === 0 ? (
          <div className="notif-empty">
            <i className="fa-regular fa-folder-open"></i>
            <p>No notifications found.</p>
          </div>
        ) : (
          notifications.map(notif => (
            <div
              key={notif._id}
              className={`notif-page-item ${!notif.isRead ? 'unread' : ''}`}
              onClick={() => handleNotificationClick(notif)}
            >
              <div className="notif-item-left">
                <div className="notif-icon-box">
                  <i className={`fa-solid ${getNotificationIcon(notif.type)}`}></i>
                </div>
                <div className="notif-info">
                  <span className="notif-title">{notif.title}</span>
                  <span className="notif-desc">{notif.message}</span>
                </div>
              </div>
              <div className="notif-item-right">
                <span className="notif-time">{getTimeAgo(notif.createdAt)}</span>
                {!notif.isRead && <div className="notif-dot"></div>}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notifications;